

// export default AppraisalForm;
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../config/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { generateAppraisalPDF } from '../../utils/generateAppraisalPDF';
import logo from '../../assets/logo.png';
import styles from './AppraisalForm.module.css';

const DESCRIPTION_TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
];

const DESCRIPTION_FORMATS = ['bold', 'italic', 'underline', 'list'];

const KRA_DATA = [
  { id: 'manufacturing', dept: 'Manufacturing / Production', icon: '🏭', items: ['Customer complaints','Customer satisfaction','Cycle time – Preparing SOPs','Asset utilization','Availability','Capacity utilization','Number of production assignments completed in time','On-time orders','On-time shipping','Overall equipment effectiveness','Overtime as a percentage of total hours','Percentage decrease in inventory carrying cost'] },
  { id: 'qa', dept: 'QA & QC Department', icon: '🔬', items: ['Efficiency Indicator – Productivity','Impact Indicator – Customer Satisfaction','Effectiveness Indicator – Value','Customer Service Indicator – Customer Complaints','Safety Indicator – Quality','Develop SOPs','Improve compliance rate','Increase daily tests executed by 10%','Improve efficiency of production equipment'] },
  { id: 'sales', dept: 'Sales', icon: '📈', items: ['Revenue','Sales turnover','Sales cycle time','Actual calls','Average revenue per product','Call quota','Closed sales','Closing ratio','Customer acquisition costs','Percentage of sales due to launched products','Percentage of sales representatives to achieve quota'] },
  { id: 'supplychain', dept: 'Supply Chain & Logistics', icon: '🚚', items: ['Order accuracy','Customer order cycle time','Inventory turnover','On time Delivery','On time Shipment','Compliance rate','Supplier defect rate','PO and invoice accuracy','Procurement Cost reduction','Warehouse Capacity','Accurate Order Fulfillment','Employee Turnover Rate'] },
  { id: 'admin', dept: 'Administration / Front Office', icon: '🏢', items: ['Reviewing the entire schedule','Gathering and communicating information','Assembling travel itineraries','Scheduling meetings','Customer service','Time management','Collaboration','Communication','Organization'] },
  { id: 'marketing', dept: 'Marketing', icon: '📣', items: ['Average response rates of campaigns','Brand awareness percentage','Consumer awareness','Customer lifetime value','Digital marketing ROI','Social media traffic and conversion rates','Mobile traffic, leads, and conversion rates'] },
  { id: 'it', dept: 'IT Department', icon: '💻', items: ['Ticket response rates','Resolution rates','Overall internal satisfaction with IT','Account create success','Active directory performance index','Service desk time to resolve','User experience','SAP query Ticket'] },
  { id: 'finance', dept: 'Finance Department', icon: '💰', items: ['Accounts payable turnover','Cash conversion cycle','Creditor days','Cycle time to perform periodic close','Debtor days','Gross profit margin','Net profit margin','Number of budget deviations','Revenue per employee','Accounts receivable'] },
  { id: 'customerservice', dept: 'Customer Service', icon: '🎧', items: ['Answering percentage','Average after-call work time','Average number of calls per handler','Average queue time','Cost per minute of handle time','E-mail backlog','Field service technician utilization'] },
];

const SATISFACTION_OPTIONS = ['Happy 😊', 'Fine 😐', 'UnHappy 😔'];
const RATING_OPTIONS = ['Requires Improvement (RI)', 'At Standard (AS)', 'Clear Strength (CS)'];
const HR_EMAIL = 'gurpreet@densitypharmachem.com';

const calcScore = (percentage, weightage) => {
  const pct = parseFloat(percentage) || 0;
  const wt  = parseFloat(weightage)  || 0;
  return parseFloat(((pct / 100) * wt).toFixed(2));
};

const makeGoal = (number) => ({
  id: number,
  goalNumber: number,
  title: `Goal: ${number}`,
  description: '',
  timeline: '',
  weightage: 0,
  expanded: number === 1,
  isDevelopment: false,
  selfMidYear:    { comments: '', percentage: 0, score: 0 },
  managerMidYear: { comments: '', percentage: 0, score: 0 },
  selfEndYear:    { comments: '', percentage: 0, score: 0 },
  managerEndYear: { comments: '', percentage: 0, score: 0 },
  hodEndYear:     { comments: '', percentage: 0, score: 0 },
});

const renumberGoals = (goals) =>
  goals.map((g, idx) => ({
    ...g,
    id: idx + 1,
    goalNumber: idx + 1,
    title: g.title.startsWith('Goal:') ? `Goal: ${idx + 1}` : g.title,
  }));

const AppraisalForm = () => {
  const { user } = useAuth();
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isViewingOther = !!employeeId;
  const isReviewMode   = searchParams.get('review') === 'true';
  const isHOD     = user?.isHOD;
  const isManager = user?.isManager;
  const isHR      = user?.isHR || user?.email?.toLowerCase() === HR_EMAIL;

  const [formData,       setFormData]       = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [saving,         setSaving]         = useState(false);
  const [submitLoading,  setSubmitLoading]  = useState(false);
  const [saveMsg,        setSaveMsg]        = useState('');
  const [error,          setError]          = useState('');
  const [selfReviewMode, setSelfReviewMode] = useState(false);
  const [pdfLoading,     setPdfLoading]     = useState(false);

  const [goals, setGoals] = useState(renumberGoals([makeGoal(1), makeGoal(2), makeGoal(3)]));

  const [section2Comments, setSection2Comments] = useState({
    employeeMidComment: '',
    managerMidComment:  '',
    hodMidComment:      '',
    employeeEndComment: '',
    managerEndComment:  '',
    hodEndComment:      '',
  });

  const [ratings, setRatings] = useState({
    selfEndRating:    '',
    managerEndRating: '',
    hodEndRating:     '',
  });

  const [satisfaction, setSatisfaction] = useState({
    satisfactionMidYear: '',
    satisfactionEndYear: '',
  });

  const [signoffDates, setSignoffDates] = useState({
    employeeGoalsDate: '2025-04-01',
    employeeMidDate:   '2025-09-30',
    employeeEndDate:   '2026-03-31',
    managerGoalsDate:  '2025-04-01',
    managerMidDate:    '2025-09-30',
    managerEndDate:    '2026-03-31',
    hodGoalsDate:      '2025-04-01',
    hodMidDate:        '2025-09-30',
    hodEndDate:        '2026-03-31',
  });

  const [signatures, setSignatures] = useState({
    employeeSignature: '',
    managerSignature:  '',
    hodSignature:      '',
  });

  const [empInfoExpanded, setEmpInfoExpanded] = useState(true);
  const [kraExpanded,     setKraExpanded]     = useState(false);
  const [kraOpenDepts,    setKraOpenDepts]    = useState({});
  const [overallExpanded, setOverallExpanded] = useState(true);
  const [signoffExpanded, setSignoffExpanded] = useState(true);

  const [periods,         setPeriods]         = useState([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);
  const [allPeriods, setAllPeriods] = useState([]);

  const formatPeriodLabel = (from, to) =>
    `${new Date(from).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} — ${new Date(to).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}`;

  useEffect(() => {
    setSelectedPeriodId(null);
    if (!isViewingOther) {
      fetchMyPeriods();
    } else {
      const urlPeriodId = searchParams.get('reviewPeriodId');
      if (urlPeriodId) setSelectedPeriodId(Number(urlPeriodId));
    }
  }, [employeeId]);

  useEffect(() => { fetchForm(); }, [employeeId, selectedPeriodId]);

  // The "latest period" and "frozen" flags are global (not tied to whose form is open), so
  // they're fetched once via the generic periods endpoint rather than the self-scoped
  // my-periods one — that endpoint isn't populated at all when viewing someone else's form.
  useEffect(() => {
    api.getReviewPeriods().then((res) => {
      if (res.success && res.data?.length) setAllPeriods(res.data);
    }).catch(() => { });
  }, []);

  const fetchMyPeriods = async () => {
    try {
      const res = await api.getMyAppraisalPeriods();
      if (res.success && res.data?.length) {
        setPeriods(res.data);
        setSelectedPeriodId((prev) => prev ?? (res.data.find((p) => p.isLatest) || res.data[0]).reviewPeriodId);
      }
    } catch { }
  };

  // Guards against out-of-order responses: if the user switches periods quickly, an
  // earlier (slower) fetch can resolve after a later one and overwrite it with stale data.
  // Only the response from the most recently fired fetchForm() call is ever applied.
  const fetchRequestIdRef = useRef(0);

  const fetchForm = async () => {
    const requestId = ++fetchRequestIdRef.current;
    setLoading(true);
    try {
      const res = isViewingOther
        ? await api.getEmployeeAppraisalForm(employeeId, selectedPeriodId)
        : await api.getMyAppraisalForm(selectedPeriodId);
      if (requestId !== fetchRequestIdRef.current) return;
      if (res.success && res.data) {
        setFormData(res.data);
        populateForm(res.data);
      }
    } catch {
      if (requestId === fetchRequestIdRef.current) setError('Failed to load form.');
    } finally {
      if (requestId === fetchRequestIdRef.current) setLoading(false);
    }
  };

  const populateForm = (data) => {
    // Always replace goals with whatever this form's data has — including resetting back
    // to the blank template when switching to a period with none — so stale goals (and the
    // score/weightage/rating computed from them) never leak across a period switch.
    const loaded = (data.goals && data.goals.length > 0)
      ? data.goals.map((g, idx) => ({
          id: idx + 1,
          goalNumber: idx + 1,
          title: g.title || `Goal: ${idx + 1}`,
          description: g.description || '',
          timeline: g.timeline || '',
          weightage: parseFloat(g.weightage) || 0,
          expanded: idx === 0,
          isDevelopment: g.isDevelopment || false,
          selfMidYear:    { comments: g.selfMidComments    || '', percentage: parseFloat(g.selfMidPercentage)    || 0, score: parseFloat(g.selfMidScore)    || 0 },
          managerMidYear: { comments: g.managerMidComments || '', percentage: parseFloat(g.managerMidPercentage) || 0, score: parseFloat(g.managerMidScore) || 0 },
          selfEndYear:    { comments: g.selfEndComments    || '', percentage: parseFloat(g.selfEndPercentage)    || 0, score: parseFloat(g.selfEndScore)    || 0 },
          managerEndYear: { comments: g.managerEndComments || '', percentage: parseFloat(g.managerEndPercentage) || 0, score: parseFloat(g.managerEndScore) || 0 },
          hodEndYear:     { comments: g.hodEndComments     || '', percentage: parseFloat(g.hodEndPercentage)     || 0, score: parseFloat(g.hodEndScore)     || 0 },
        }))
      : renumberGoals([makeGoal(1), makeGoal(2), makeGoal(3)]);
    setGoals(loaded);
    setSection2Comments({
      employeeMidComment: data.employeeMidComment || '',
      managerMidComment:  data.managerMidComment  || '',
      hodMidComment:      data.hodMidComment      || '',
      employeeEndComment: data.employeeEndComment || '',
      managerEndComment:  data.managerEndComment  || '',
      hodEndComment:      data.hodEndComment      || '',
    });
    setRatings({
      selfEndRating:    data.selfEndRating    || '',
      managerEndRating: data.managerEndRating || '',
      hodEndRating:     data.hodEndRating     || '',
    });
    setSatisfaction({
      satisfactionMidYear: data.satisfactionMidYear || '',
      satisfactionEndYear: data.satisfactionEndYear || '',
    });
    setSignoffDates({
      employeeGoalsDate: data.employeeGoalsDate || '2025-04-01',
      employeeMidDate:   data.employeeMidDate   || '2025-09-30',
      employeeEndDate:   data.employeeEndDate   || '2026-03-31',
      managerGoalsDate:  data.managerGoalsDate  || '2025-04-01',
      managerMidDate:    data.managerMidDate    || '2025-09-30',
      managerEndDate:    data.managerEndDate    || '2026-03-31',
      hodGoalsDate:      data.hodGoalsDate      || '2025-04-01',
      hodMidDate:        data.hodMidDate        || '2025-09-30',
      hodEndDate:        data.hodEndDate        || '2026-03-31',
    });
    setSignatures({
      employeeSignature: data.employeeSignature || '',
      managerSignature:  data.managerSignature  || '',
      hodSignature:      data.hodSignature      || '',
    });
  };

  // ===== PERMISSIONS =====
  const employeeSubmitted = formData?.employeeSubmitted || false;
  const managerSubmitted  = formData?.managerSubmitted  || false;
  const hodSubmitted      = formData?.hodSubmitted      || false;

  const effectiveReadOnly = isReviewMode || selfReviewMode;

  const viewerFirstName   = user?.name?.split(' ')[0]?.toLowerCase();
  const reportingMgrLower = formData?.reportingManager?.toLowerCase() || '';
  const isDirectReport    =
    reportingMgrLower === viewerFirstName ||
    viewerFirstName?.includes(reportingMgrLower) ||
    reportingMgrLower?.includes(viewerFirstName);

  // The period this specific form belongs to, looked up from the global periods list.
  const formPeriodMeta = allPeriods.find((p) => p.id === formData?.reviewPeriodId);
  const isFrozenPeriod = !!formPeriodMeta?.isFrozen;

  const hrFullAccess = isHR && !effectiveReadOnly && !isFrozenPeriod;

  // Deadline only blocks plain employees filling their own form — HR/manager/HOD are unaffected.
  // Sourced from the selected review period's deadline instead of a global constant.
  const currentPeriodMeta = periods.find((p) => p.reviewPeriodId === selectedPeriodId) || periods.find((p) => p.isLatest);
  const isPastDeadline = currentPeriodMeta?.deadlineDate
    ? new Date() > new Date(`${currentPeriodMeta.deadlineDate}T23:59:59`)
    : false;
  const employeeDeadlineBlock = isPastDeadline && !isManager && !isHOD && !isHR;
  const newerOpenPeriod = !isViewingOther && currentPeriodMeta
    ? periods.find((p) => new Date(p.startDate) > new Date(currentPeriodMeta.startDate) && !p.employeeSubmitted)
    : null;

  // Whether the form being viewed belongs to the latest review period — only the latest
  // period allows re-editing after submission (older periods stay locked once submitted).
  const isLatestPeriod = !!formPeriodMeta?.isLatest;

  // Reporting manager (not HOD) may open and edit an employee's form in any state — draft,
  // not started, or submitted — and edit the employee-part fields too, but only for the
  // latest period. Also stays editable after the manager's own submission (re-submission),
  // same as the latest-period employee resubmission rule above.
  const managerCanEditEmployeeFields =
    !effectiveReadOnly && !isFrozenPeriod && isViewingOther && isManager && isDirectReport && isLatestPeriod;

  const canEditEmployee = hrFullAccess
    || (!effectiveReadOnly && !isFrozenPeriod && !isViewingOther && !employeeDeadlineBlock && (!employeeSubmitted || isLatestPeriod))
    || managerCanEditEmployeeFields;

  const canEditManager = hrFullAccess || (
    !effectiveReadOnly && !isFrozenPeriod && isViewingOther && isDirectReport &&
    (
      (isManager && (isLatestPeriod || (employeeSubmitted && !managerSubmitted))) ||
      (isHOD && employeeSubmitted && !managerSubmitted)
    )
  );

  const canEditHOD = hrFullAccess || (
    !effectiveReadOnly && !isFrozenPeriod && !hodSubmitted &&
    isViewingOther && isHOD && (managerSubmitted || isDirectReport)
  );

  // ===== GOAL HANDLERS =====
  const toggleGoal = (id) =>
    setGoals((p) => p.map((g) => g.id === id ? { ...g, expanded: !g.expanded } : g));

  const updateGoalField = (id, field, value) =>
    setGoals((p) => p.map((g) => g.id === id ? { ...g, [field]: value } : g));

  const updateReviewPercentage = (id, reviewType, value) => {
    setGoals((p) => p.map((g) => {
      if (g.id !== id) return g;
      const score = calcScore(value, g.weightage);
      return { ...g, [reviewType]: { ...g[reviewType], percentage: value, score } };
    }));
  };

  const updateWeightage = (id, value) => {
    setGoals((p) => p.map((g) => {
      if (g.id !== id) return g;
      const wt = parseFloat(value) || 0;
      return {
        ...g,
        weightage: value,
        selfMidYear:    { ...g.selfMidYear,    score: calcScore(g.selfMidYear.percentage,    wt) },
        managerMidYear: { ...g.managerMidYear, score: calcScore(g.managerMidYear.percentage, wt) },
        selfEndYear:    { ...g.selfEndYear,    score: calcScore(g.selfEndYear.percentage,    wt) },
        managerEndYear: { ...g.managerEndYear, score: calcScore(g.managerEndYear.percentage, wt) },
        hodEndYear:     { ...g.hodEndYear,     score: calcScore(g.hodEndYear.percentage,     wt) },
      };
    }));
  };

  const updateReviewComments = (id, reviewType, value) =>
    setGoals((p) => p.map((g) =>
      g.id === id ? { ...g, [reviewType]: { ...g[reviewType], comments: value } } : g
    ));

  const addGoal = () => {
    if (goals.length >= 5) return;
    setGoals((p) => renumberGoals([...p, makeGoal(p.length + 1)]));
  };

  const removeGoal = (id) => {
    if (goals.length <= 1) return;
    setGoals((p) => renumberGoals(p.filter((g) => g.id !== id)));
  };

  // ===== COMPUTED =====
  const totalWeightage = goals.reduce((sum, g) => sum + (parseFloat(g.weightage) || 0), 0);

  const getGoalFinalScore = (g) => {
    if (parseFloat(g.hodEndYear.score)     > 0) return parseFloat(g.hodEndYear.score);
    if (parseFloat(g.managerEndYear.score) > 0) return parseFloat(g.managerEndYear.score);
    return parseFloat(g.selfEndYear.score) || 0;
  };

  const overallScore = goals.reduce((sum, g) => sum + getGoalFinalScore(g), 0);

  const getWeightageColor = () =>
    totalWeightage === 100 ? 'var(--color-green)' :
    totalWeightage > 100   ? '#e53e3e' : '#f6ad55';

  const getOverallRating = (score) => {
    if (score >= 90) return 'CS';
    if (score >= 65) return 'AS';
    return 'RI';
  };

  // ===== SIGNATURE UPLOAD =====
  const handleSignatureUpload = (field, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSignatures((p) => ({ ...p, [field]: ev.target.result }));
    reader.readAsDataURL(file);
  };

  // const SignatureCell = ({ field, canEdit }) => (
  //   <div className={styles.signoffCell}>
  //     {signatures[field] ? (
  //       <div className={styles.signaturePreview}>
  //         <img src={signatures[field]} alt="Signature" className={styles.signatureImg} />
  //         {canEdit && (
  //           <button className={styles.signatureRemove}
  //             onClick={() => setSignatures((p) => ({ ...p, [field]: '' }))}>
  //             Remove
  //           </button>
  //         )}
  //       </div>
  //     ) : canEdit ? (
  //       <label className={styles.signatureUpload}>
  //         Upload Signature
  //         <input type="file" accept="image/*" style={{ display: 'none' }}
  //           onChange={(e) => handleSignatureUpload(field, e.target.files[0])} />
  //       </label>
  //     ) : (
  //       <span style={{ color: 'var(--color-text-light)', fontSize: 11 }}>—</span>
  //     )}
  //   </div>
  // );

  const SignatureCell = ({ field, canEdit }) => (
  <div className={styles.signoffCell}>
    {signatures[field] ? (
      <div className={styles.signaturePreview}>
        <img src={signatures[field]} alt="Signature" className={styles.signatureImg} />
        {canEdit && (
          <label className={styles.signatureChange}>
            Change
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => handleSignatureUpload(field, e.target.files[0])} />
          </label>
        )}
      </div>
    ) : canEdit ? (
      <label className={styles.signatureUpload}>
        Upload Signature
        <input type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => handleSignatureUpload(field, e.target.files[0])} />
      </label>
    ) : (
      <span style={{ color: 'var(--color-text-light)', fontSize: 11 }}>—</span>
    )}
  </div>
);


  // ===== PAYLOAD =====
  const buildPayload = (submitType) => ({
    goals: goals.map((g) => ({
      goalNumber:           g.goalNumber,
      title:                g.title,
      description:          g.description,
      timeline:             g.timeline,
      percentage:           0,
      weightage:            parseFloat(g.weightage) || 0,
      isDevelopment:        g.isDevelopment,
      selfMidComments:      g.selfMidYear.comments,
      selfMidPercentage:    parseFloat(g.selfMidYear.percentage)    || 0,
      selfMidScore:         g.selfMidYear.score,
      managerMidComments:   g.managerMidYear.comments,
      managerMidPercentage: parseFloat(g.managerMidYear.percentage) || 0,
      managerMidScore:      g.managerMidYear.score,
      selfEndComments:      g.selfEndYear.comments,
      selfEndPercentage:    parseFloat(g.selfEndYear.percentage)    || 0,
      selfEndScore:         g.selfEndYear.score,
      managerEndComments:   g.managerEndYear.comments,
      managerEndPercentage: parseFloat(g.managerEndYear.percentage) || 0,
      managerEndScore:      g.managerEndYear.score,
      hodEndComments:       g.hodEndYear.comments,
      hodEndPercentage:     parseFloat(g.hodEndYear.percentage)     || 0,
      hodEndScore:          g.hodEndYear.score,
    })),
    ...section2Comments,
    ...ratings,
    ...satisfaction,
    ...signoffDates,
    ...signatures,
    overallScore:  parseFloat(overallScore.toFixed(2)),
    overallRating: getOverallRating(overallScore),
    submitType,
  });

  // const handleSaveDraft = async () => {
  //   setSaving(true); setSaveMsg(''); setError('');
  //   try {
  //     const res = await api.saveAppraisalDraft(buildPayload('EMPLOYEE'));
  //     if (res.success) { setSaveMsg('Draft saved!'); setTimeout(() => setSaveMsg(''), 3000); }
  //     else setError(res.message);
  //   } catch { setError('Failed to save.'); }
  //   finally { setSaving(false); }
  // };
  const handleSaveDraft = async () => {
  if (loading || !goals || goals.length === 0) {
    setError('Form is still loading. Please wait a moment and try again.');
    return;
  }
  setSaving(true); setSaveMsg(''); setError('');
  try {
    const res = await api.saveAppraisalDraft(buildPayload('EMPLOYEE'), selectedPeriodId);
    if (res.success) { setSaveMsg('Draft saved!'); setTimeout(() => setSaveMsg(''), 3000); }
    else setError(res.message);
  } catch { setError('Failed to save.'); }
  finally { setSaving(false); }
};

  const handleSubmit = async (submitType) => {
    setSubmitLoading(true); setError('');
    try {
      const targetId = isViewingOther ? Number(employeeId) : null;
      const res = await api.submitAppraisal(buildPayload(submitType), targetId, selectedPeriodId);
      if (res.success) {
        setSaveMsg(`${submitType === 'EMPLOYEE' ? 'Form' : submitType === 'MANAGER' ? 'Manager review' : 'HOD review'} submitted!`);
        fetchForm();
      } else setError(res.message);
    } catch { setError('Failed to submit.'); }
    finally { setSubmitLoading(false); }
  };

  // ===== PDF DOWNLOAD (own submitted form) =====
  const handleDownloadPDF = async () => {
    if (!formData) return;
    setPdfLoading(true);
    try {
      const response = await fetch(logo);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = () => { generateAppraisalPDF(formData, reader.result); setPdfLoading(false); };
      reader.onerror = () => { generateAppraisalPDF(formData, null); setPdfLoading(false); };
      reader.readAsDataURL(blob);
    } catch {
      generateAppraisalPDF(formData, null);
      setPdfLoading(false);
    }
  };

  // ===== DISPLAY VALUES =====
  const displayName    = isViewingOther ? formData?.employeeName     : user?.name;
  const displayCode    = isViewingOther ? formData?.employeeCode     : user?.employeeCode;
  const displayDesig   = isViewingOther ? formData?.designation      : user?.designation;
  const displayDept    = isViewingOther ? formData?.department       : user?.department;
  const displayBranch  = isViewingOther ? formData?.branch           : user?.branch;
  const displayJoining = isViewingOther ? formData?.dateOfJoining    : user?.dateOfJoining;
  const displayManager = isViewingOther ? formData?.reportingManager : user?.reportingManager;

  const formatDate = (dt) => dt
    ? new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px', color: 'var(--color-text-light)' }}>
      Loading appraisal form...
    </div>
  );

  // ===== REVIEW PERIOD SWITCHER (shown in the header at every stage) =====
  const renderPeriodSwitcher = () => (
    <div className={styles.reviewPeriod}>
      <span className={styles.reviewLabel}>Review Period</span>
      {!isViewingOther && periods.length > 1 ? (
        <select
          className={styles.reviewPeriodSelect}
          value={selectedPeriodId || ''}
          onChange={(e) => setSelectedPeriodId(Number(e.target.value))}>
          {periods.map((p) => (
            <option key={p.reviewPeriodId} value={p.reviewPeriodId}>
              {formatPeriodLabel(p.startDate, p.endDate)}{p.isLatest ? ' (Latest)' : ''}
            </option>
          ))}
        </select>
      ) : (
        <span className={styles.reviewValue}>
          {formData?.reviewPeriodFrom && formData?.reviewPeriodTo
            ? formatPeriodLabel(formData.reviewPeriodFrom, formData.reviewPeriodTo)
            : 'Apr 2025 — Mar 2026'}
        </span>
      )}
    </div>
  );

  // ===== DEADLINE PASSED SCREEN (employee never submitted, window closed) =====
  if (!isViewingOther && !effectiveReadOnly && !employeeSubmitted && employeeDeadlineBlock) return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>Performance Appraisal & Development Form</h1>
          <span className={styles.pageBadge}>PMD</span>
        </div>
        {renderPeriodSwitcher()}
      </div>
      <div className={styles.submittedCard}>
        <div className={styles.submittedTop}>
          <div className={styles.submittedIcon}>⏰</div>
          <div>
            <h2 className={styles.submittedTitle}>Submission Deadline Has Passed</h2>
            <p className={`${styles.submittedDesc} ${styles.deadlineDesc}`}>
              The deadline to fill this form was <strong>{formatDate(currentPeriodMeta?.deadlineDate)}</strong>.
              Since the form was not submitted before the deadline, it can no longer be filled or edited.
              Please contact HR if you believe this is a mistake.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // ===== SUBMITTED SCREEN ===== (latest period stays editable even after submission, so it
  // skips this locked card and falls through to the full form below)
  if (!isViewingOther && !effectiveReadOnly && employeeSubmitted && !isLatestPeriod && !isManager && !isHOD && !isHR) return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>Performance Appraisal & Development Form</h1>
          <span className={styles.pageBadge}>PMD</span>
        </div>
        {renderPeriodSwitcher()}
      </div>
      {newerOpenPeriod && (
        <div className={styles.newPeriodBanner}>
          🆕 A new review period, <strong>{formatPeriodLabel(newerOpenPeriod.startDate, newerOpenPeriod.endDate)}</strong>, is open.
          Switch to it above to start your next appraisal.
        </div>
      )}
      <div className={styles.submittedCard}>
        <div className={styles.submittedTop}>
          <div className={styles.submittedIcon}>✅</div>
          <div>
            <h2 className={styles.submittedTitle}>Your Appraisal Has Been Submitted!</h2>
            <p className={styles.submittedDesc}>
              Track the review progress below. You will be notified once your manager and HOD complete their reviews.
            </p>
          </div>
        </div>
        <div className={styles.progressSteps}>
          <div className={styles.progressStep}>
            <div className={`${styles.progressDot} ${styles.progressDotDone}`}>✅</div>
            <div className={styles.progressInfo}>
              <span className={styles.progressLabel}>Employee Submitted</span>
              <span className={styles.progressDate}>{formatDate(formData?.employeeSubmittedAt) || 'Done'}</span>
            </div>
          </div>
          <div className={styles.progressLine} />
          <div className={styles.progressStep}>
            <div className={`${styles.progressDot} ${managerSubmitted ? styles.progressDotDone : styles.progressDotPending}`}>
              {managerSubmitted ? '✅' : '⏳'}
            </div>
            <div className={styles.progressInfo}>
              <span className={styles.progressLabel}>Manager Review</span>
              <span className={styles.progressDate}>
                {managerSubmitted ? formatDate(formData?.managerSubmittedAt) || 'Completed' : 'Pending'}
              </span>
            </div>
          </div>
          <div className={styles.progressLine} />
          <div className={styles.progressStep}>
            <div className={`${styles.progressDot} ${hodSubmitted ? styles.progressDotDone : styles.progressDotPending}`}>
              {hodSubmitted ? '✅' : '⏳'}
            </div>
            <div className={styles.progressInfo}>
              <span className={styles.progressLabel}>HOD Review</span>
              <span className={styles.progressDate}>
                {hodSubmitted ? formatDate(formData?.hodSubmittedAt) || 'Completed' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.submittedActions}>
          <button className={styles.reviewFormBtn} onClick={() => setSelfReviewMode(true)}>
            View My Submitted Form
          </button>
          <button className={styles.reviewFormBtn} onClick={handleDownloadPDF} disabled={pdfLoading}>
            {pdfLoading ? 'Generating PDF...' : '📄 Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );

  // ===== FULL FORM =====
  return (
    <div className={styles.container}>

      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageTitle}>Performance Appraisal & Development Form</h1>
          <span className={styles.pageBadge}>PMD</span>
        </div>
        {renderPeriodSwitcher()}
      </div>

      {/* Status Bar */}
      <div className={styles.statusBar}>
        <div className={`${styles.statusStep} ${employeeSubmitted ? styles.statusDone : styles.statusPending}`}>
          <span className={styles.statusIcon}>{employeeSubmitted ? '✅' : '⏳'}</span>
          <span>Employee</span>
        </div>
        <div className={styles.statusLine} />
        <div className={`${styles.statusStep} ${managerSubmitted ? styles.statusDone : employeeSubmitted ? styles.statusActive : styles.statusPending}`}>
          <span className={styles.statusIcon}>{managerSubmitted ? '✅' : '⏳'}</span>
          <span>Manager</span>
        </div>
        <div className={styles.statusLine} />
        <div className={`${styles.statusStep} ${hodSubmitted ? styles.statusDone : managerSubmitted ? styles.statusActive : styles.statusPending}`}>
          <span className={styles.statusIcon}>{hodSubmitted ? '✅' : '⏳'}</span>
          <span>HOD</span>
        </div>
      </div>

      {/* Banner */}
      {(isViewingOther || selfReviewMode) && (
        <div className={effectiveReadOnly ? styles.reviewModeBanner : styles.viewingBanner}>
          <span>
            {effectiveReadOnly ? `👁 Review Mode — Read Only | ` : '✏️ Editing: '}
            <strong>{displayName}</strong>
          </span>
          {isViewingOther
            ? <button className={styles.backBtn} onClick={() => navigate('/appraisal-report')}>← Back</button>
            : <button className={styles.backBtn} onClick={() => setSelfReviewMode(false)}>← Back</button>
          }
        </div>
      )}

      {isFrozenPeriod && (
        <div className={styles.frozenFormBanner}>
          🔒 This review period is frozen — no one (employee, manager, HOD, or HR) can edit or submit this form anymore.
        </div>
      )}

      {isHR && isViewingOther && !effectiveReadOnly && !isFrozenPeriod && (
        <div className={styles.hrAccessBanner}>
          👑 HR Full Access — You can edit and submit at any stage
        </div>
      )}

      {error && <div className={styles.errorMsg}>{error}</div>}
      {saveMsg && <div className={`${styles.successMsg} ${styles.toast}`}>✅ {saveMsg}</div>}

      {/* KRA Guide */}
      <div className={styles.kraGuide}>
        <div className={styles.collapsibleHeader} onClick={() => setKraExpanded(!kraExpanded)}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardIcon}>📚</span>
            <div>
              <span className={styles.cardTitle}>KRA / KPI Reference Guide</span>
              <span className={styles.cardSubtitle}> — Review before setting goals</span>
            </div>
          </div>
          <span className={styles.toggleIcon}>{kraExpanded ? '▼' : '▶'}</span>
        </div>
        {kraExpanded && (
          <div className={styles.kraBody}>
            <div className={styles.kraHint}>💡 Select KPIs relevant to your department when defining goals.</div>
            <div className={styles.kraDeptGrid}>
              {KRA_DATA.map((dept) => (
                <div key={dept.id} className={styles.kraDeptCard}>
                  <div className={styles.kraDeptHeader}
                    onClick={() => setKraOpenDepts((p) => ({ ...p, [dept.id]: !p[dept.id] }))}>
                    <span className={styles.kraDeptIcon}>{dept.icon}</span>
                    <span className={styles.kraDeptName}>{dept.dept}</span>
                    <span className={styles.kraDeptCount}>{dept.items.length} KPIs</span>
                    <span className={styles.kraToggle}>{kraOpenDepts[dept.id] ? '▼' : '▶'}</span>
                  </div>
                  {kraOpenDepts[dept.id] && (
                    <ul className={styles.kraList}>
                      {dept.items.map((item, i) => <li key={i} className={styles.kraItem}>{item}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Employee Info */}
      <div className={styles.card}>
        <div className={styles.collapsibleHeader} onClick={() => setEmpInfoExpanded(!empInfoExpanded)}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardIcon}>👤</span>
            <span className={styles.cardTitle}>Employee Information</span>
          </div>
          <span className={styles.toggleIcon}>{empInfoExpanded ? '▼' : '▶'}</span>
        </div>
        {empInfoExpanded && (
          <div className={styles.detailsGrid}>
            {[
              { label: 'Employee Code',     value: displayCode    },
              { label: 'Employee Name',     value: displayName    },
              { label: 'Designation',       value: displayDesig   },
              { label: 'Department',        value: displayDept    },
              { label: 'Office Location',   value: displayBranch  },
              { label: 'Date of Joining',   value: displayJoining },
              { label: 'Reporting Manager', value: displayManager },
              { label: 'HOD',               value: 'Ramakrishnan Sundaram' },
            ].map(({ label, value }) => (
              <div key={label} className={styles.detailItem}>
                <span className={styles.detailLabel}>{label}</span>
                <span className={styles.detailValue}>{value || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Score Summary */}
      <div className={styles.scoreSummary}>
        <div className={styles.scoreCard}>
          <div className={styles.scoreValue} style={{ color: 'var(--color-secondary)' }}>
            {overallScore.toFixed(1)}
          </div>
          <div className={styles.scoreLabel}>Overall Score (/ 100)</div>
        </div>
        <div className={styles.scoreCard}>
          <div className={styles.scoreValue} style={{ color: getWeightageColor() }}>
            {totalWeightage}
          </div>
          <div className={styles.scoreLabel}>
            Total Weightage {totalWeightage !== 100 && <span className={styles.weightageWarn}>(must = 100)</span>}
          </div>
        </div>
        <div className={styles.scoreCard}>
          <div className={styles.scoreValue} style={{
            color: overallScore >= 90 ? 'var(--color-green-dark)' :
                   overallScore >= 65 ? '#d97706' :
                   overallScore > 0   ? '#e53e3e' : 'var(--color-text-light)'
          }}>
            {overallScore > 0 ? getOverallRating(overallScore) : '—'}
          </div>
          <div className={styles.scoreLabel}>
            Rating {overallScore > 0 ? `(${overallScore.toFixed(1)})` : ''}
          </div>
        </div>
        <div className={styles.scoreCard}>
          <div className={styles.scoreValue} style={{ color: 'var(--color-primary)' }}>
            {goals.length}
          </div>
          <div className={styles.scoreLabel}>Total Goals</div>
        </div>
      </div>

      {/* Section 1 */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderLeft}>
          <span className={styles.sectionBadge}>Section 1</span>
          <span className={styles.sectionTitle}>Performance Goals</span>
        </div>
        <span className={styles.colHint}>Weightage must add up to 100</span>
      </div>

      {goals.map((goal) => (
        <div key={goal.id} className={styles.goalCard}>
          <div className={styles.goalHeader} onClick={() => toggleGoal(goal.id)}>
            {/* <div className={styles.goalHeaderLeft}>
              <span className={styles.goalToggle}>{goal.expanded ? '▼' : '▶'}</span>
              <span className={styles.goalTitle}>🎯 {goal.title}</span>
              
            </div> */}
            <div className={styles.goalHeaderLeft}>
              <span className={styles.goalToggle}>{goal.expanded ? '▼' : '▶'}</span>
              <span className={styles.goalNum}>Goal {goal.goalNumber}</span>
              {canEditEmployee ? (
                <input
                  className={styles.goalHeadingInput}
                  type="text"
                  placeholder="Enter goal heading..."
                  value={goal.title.startsWith(`Goal: ${goal.goalNumber}`) ? '' : goal.title}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const val = e.target.value;
                    updateGoalField(goal.id, 'title', val || `Goal: ${goal.goalNumber}`);
                  }}
                />
              ) : (
                <span className={styles.goalHeadingText}>
                  {goal.title.startsWith('Goal:') ? '' : `— ${goal.title}`}
                </span>
              )}
            </div>
            <div className={styles.goalHeaderRight}>
              <div className={styles.goalMeta}>
                <span className={styles.metaLabel}>Weightage</span>
                <input className={styles.metaInput} type="number" min="0" max="100"
                  value={goal.weightage}
                  disabled={!canEditEmployee}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => updateWeightage(goal.id, e.target.value)} />
              </div>
              <div className={styles.endScorePill}>
                Score: <strong>{getGoalFinalScore(goal).toFixed(1)}</strong>
              </div>
              {canEditEmployee && goals.length > 1 && (
                <button className={styles.removeGoalBtn}
                  onClick={(e) => { e.stopPropagation(); removeGoal(goal.id); }}>✕</button>
              )}
            </div>
          </div>

          {goal.expanded && (
            <div className={styles.goalBody}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Details Description</label>
                {canEditEmployee ? (
                  <ReactQuill className={styles.descriptionEditor}
                    theme="snow"
                    placeholder="Describe this goal..."
                    value={goal.description}
                    onChange={(value) => updateGoalField(goal.id, 'description', value)}
                    modules={{ toolbar: DESCRIPTION_TOOLBAR }}
                    formats={DESCRIPTION_FORMATS} />
                ) : (
                  <div className={styles.descriptionView}
                    dangerouslySetInnerHTML={{ __html: goal.description || '<p style="color: var(--color-text-light)">No description provided.</p>' }} />
                )}
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                  Timeline
                  <span className={styles.fieldHint}> — Q1, Q2, H1 (1st half), H2 (2nd half)</span>
                </label>
                <input className={styles.input} type="text"
                  disabled={!canEditEmployee}
                  placeholder="e.g. by H1 till Sept"
                  value={goal.timeline}
                  onChange={(e) => updateGoalField(goal.id, 'timeline', e.target.value)} />
              </div>

              <div className={styles.reviewTable}>
                <div className={styles.reviewTableHeader}>
                  <span>Review Type</span>
                  <span>Comments</span>
                  <span>Achievement %</span>
                  <span>Score (auto)</span>
                </div>

                {[
                  { key: 'selfMidYear',    label: 'Self Mid-Year',    type: 'self-mid',    canEdit: canEditEmployee },
                  { key: 'managerMidYear', label: 'Manager Mid-Year', type: 'manager-mid', canEdit: canEditManager  },
                  { key: 'selfEndYear',    label: 'Self End-Year',    type: 'self-end',    canEdit: canEditEmployee },
                  { key: 'managerEndYear', label: 'Manager End-Year', type: 'manager-end', canEdit: canEditManager  },
                  { key: 'hodEndYear',     label: 'HOD End-Year',     type: 'hod',         canEdit: canEditHOD      },
                ].map(({ key, label, type, canEdit }) => (
                  <div key={key} className={`${styles.reviewRow} ${!canEdit ? styles.disabledRow : ''}`}>
                    <div className={styles.reviewType}>
                      <span className={styles.reviewBadge} data-type={type}>{label}</span>
                    </div>
                    <textarea className={styles.reviewComment} rows={2}
                      disabled={!canEdit}
                      placeholder={`${label} comments...`}
                      value={goal[key].comments}
                      onChange={(e) => updateReviewComments(goal.id, key, e.target.value)} />
                    <input className={styles.reviewNum} type="number" min="0" max="100"
                      disabled={!canEdit}
                      value={goal[key].percentage}
                      onChange={(e) => updateReviewPercentage(goal.id, key, e.target.value)} />
                    <div className={styles.reviewScoreBox}>
                      <span className={styles.reviewScore}>{Number(goal[key].score).toFixed(1)}</span>
                      <span className={styles.reviewScoreFormula}>
                        = {goal[key].percentage || 0}% x {goal.weightage || 0} / 100
                      </span>
                    </div>
                  </div>
                ))}

                <div className={styles.reviewRowTotal}>
                  <span>Final Goal Score</span>
                  <span /><span />
                  <span className={styles.totalScore}>{getGoalFinalScore(goal).toFixed(1)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {canEditEmployee && goals.length < 5 && (
        <button className={styles.addGoalBtn} onClick={addGoal}>+ Add Goal</button>
      )}

      {/* Section 2 */}
      <div className={styles.sectionHeader} style={{ marginTop: '24px' }}>
        <div className={styles.sectionHeaderLeft}>
          <span className={styles.sectionBadge} style={{ background: 'var(--color-secondary)' }}>Section 2</span>
          <span className={styles.sectionTitle}>Employee, Manager & HOD Feedback</span>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.collapsibleHeader} onClick={() => setOverallExpanded(!overallExpanded)}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardIcon}>📊</span>
            <span className={styles.cardTitle}>Overall Comments & Feedback</span>
          </div>
          <span className={styles.toggleIcon}>{overallExpanded ? '▼' : '▶'}</span>
        </div>

        {overallExpanded && (
          <div className={styles.cardBody}>

            {/* Job Satisfaction */}
            <div className={styles.satisfactionRow}>
              {[
                { key: 'satisfactionMidYear', label: 'Job Satisfaction — Mid-Year', canEdit: canEditEmployee },
                { key: 'satisfactionEndYear', label: 'Job Satisfaction — End-Year', canEdit: canEditEmployee },
              ].map(({ key, label, canEdit }) => (
                <div key={key} className={styles.satisfactionItem}>
                  <label className={styles.fieldLabel}>{label}</label>
                  <div className={styles.satisfactionOptions}>
                    {SATISFACTION_OPTIONS.map((opt) => (
                      <button key={opt}
                        disabled={!canEdit}
                        className={`${styles.satisfactionBtn} ${satisfaction[key] === opt ? styles.satisfactionActive : ''}`}
                        onClick={() => setSatisfaction((p) => ({ ...p, [key]: opt }))}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Mid Year Comments */}
            <div className={styles.commentsSection}>
              <div className={styles.commentsPeriod}>
                <div className={styles.commentsPeriodTitle}>📅 Mid-Year Comments</div>
                <div className={styles.commentsGrid}>
                  <div className={`${styles.fieldGroup} ${!canEditEmployee ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>Employee Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditEmployee}
                      placeholder="Employee mid-year comment..."
                      value={section2Comments.employeeMidComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, employeeMidComment: e.target.value }))} />
                  </div>
                  <div className={`${styles.fieldGroup} ${!canEditManager ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>Manager Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditManager}
                      placeholder={canEditManager ? 'Manager mid-year comment...' : 'Available after employee submits'}
                      value={section2Comments.managerMidComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, managerMidComment: e.target.value }))} />
                  </div>
                  <div className={`${styles.fieldGroup} ${!canEditHOD ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>HOD Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditHOD}
                      placeholder="HOD mid-year comment..."
                      value={section2Comments.hodMidComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, hodMidComment: e.target.value }))} />
                  </div>
                </div>
              </div>

              {/* End Year Comments */}
              <div className={styles.commentsPeriod}>
                <div className={styles.commentsPeriodTitle}>📅 End-Year Comments</div>
                <div className={styles.commentsGrid}>
                  <div className={`${styles.fieldGroup} ${!canEditEmployee ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>Employee Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditEmployee}
                      placeholder="Employee end-year comment..."
                      value={section2Comments.employeeEndComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, employeeEndComment: e.target.value }))} />
                  </div>
                  <div className={`${styles.fieldGroup} ${!canEditManager ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>Manager Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditManager}
                      placeholder={canEditManager ? 'Manager end-year comment...' : 'Available after employee submits'}
                      value={section2Comments.managerEndComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, managerEndComment: e.target.value }))} />
                  </div>
                  <div className={`${styles.fieldGroup} ${!canEditHOD ? styles.disabledField : ''}`}>
                    <label className={styles.fieldLabel}>HOD Comment</label>
                    <textarea className={styles.textarea} rows={3}
                      disabled={!canEditHOD}
                      placeholder="HOD end-year comment..."
                      value={section2Comments.hodEndComment}
                      onChange={(e) => setSection2Comments((p) => ({ ...p, hodEndComment: e.target.value }))} />
                  </div>
                </div>
              </div>
            </div>

            {/* Ratings */}
            <div className={styles.ratingsSection}>
              <div className={styles.ratingsTitle}>Overall Ratings — End Year</div>
              <div className={styles.ratingInfo}>
                <span className={styles.ratingInfoItem} data-level="ri">RI: below 65</span>
                <span className={styles.ratingInfoItem} data-level="as">AS: 65 – 90</span>
                <span className={styles.ratingInfoItem} data-level="cs">CS: above 90</span>
              </div>
              {[
                { key: 'selfEndRating',    label: 'Self End-Year Rating',     canEdit: canEditEmployee },
                { key: 'managerEndRating', label: 'Reporting Manager Rating', canEdit: canEditManager  },
                { key: 'hodEndRating',     label: 'HOD Rating',               canEdit: canEditHOD      },
              ].map(({ key, label, canEdit }) => (
                <div key={key} className={`${styles.ratingRow} ${!canEdit ? styles.disabledRow : ''}`}>
                  <span className={styles.ratingLabel}>{label}</span>
                  <div className={styles.ratingOptions}>
                    {RATING_OPTIONS.map((opt) => (
                      <button key={opt}
                        disabled={!canEdit}
                        className={`${styles.ratingBtn} ${ratings[key] === opt ? styles.ratingActive : ''}`}
                        onClick={() => setRatings((p) => ({ ...p, [key]: opt }))}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>

      {/* Sign Off */}
      <div className={styles.card}>
        <div className={styles.collapsibleHeader} onClick={() => setSignoffExpanded(!signoffExpanded)}>
          <div className={styles.cardHeaderLeft}>
            <span className={styles.cardIcon}>✍️</span>
            <span className={styles.cardTitle}>Appraisal Sign Off</span>
          </div>
          <span className={styles.toggleIcon}>{signoffExpanded ? '▼' : '▶'}</span>
        </div>
        {signoffExpanded && (
          <div className={styles.cardBody}>
            <p className={styles.signoffHint}>
              Goals Setup: Apr 1 • Mid-Year: Sep 30 • End Year: Mar 31 — Upload signature in Signature column
            </p>
            <div className={styles.signoffTable}>
              <div className={styles.signoffHeader}>
                <span>Name</span>
                <span>Goals Set-up (Apr 2025)</span>
                <span>Mid-Year (Sep 30)</span>
                <span>End Year (Mar 2026)</span>
                <span>Signature</span>
              </div>

              {/* Employee Row */}
              <div className={styles.signoffRow}>
                <span className={styles.signoffName}>
                  {displayName || 'Employee'}
                  {!isViewingOther && <span className={styles.signoffYou}> (You)</span>}
                </span>
                {[
                  { field: 'employeeGoalsDate', def: '2025-04-01' },
                  { field: 'employeeMidDate',   def: '2025-09-30' },
                  { field: 'employeeEndDate',   def: '2026-03-31' },
                ].map(({ field, def }) => (
                  <div key={field} className={styles.signoffCell}>
                    <input className={styles.signoffInput} type="date"
                      disabled={!canEditEmployee}
                      value={signoffDates[field] || def}
                      onChange={(e) => setSignoffDates((p) => ({ ...p, [field]: e.target.value }))} />
                  </div>
                ))}
                <SignatureCell field="employeeSignature" canEdit={canEditEmployee} />
              </div>

              {/* Manager Row */}
              <div className={styles.signoffRow}>
                <span className={styles.signoffName}>Reporting Manager</span>
                {[
                  { field: 'managerGoalsDate', def: '2025-04-01' },
                  { field: 'managerMidDate',   def: '2025-09-30' },
                  { field: 'managerEndDate',   def: '2026-03-31' },
                ].map(({ field, def }) => (
                  <div key={field} className={styles.signoffCell}>
                    <input className={styles.signoffInput} type="date"
                      disabled={!canEditManager}
                      value={signoffDates[field] || def}
                      onChange={(e) => setSignoffDates((p) => ({ ...p, [field]: e.target.value }))} />
                  </div>
                ))}
                <SignatureCell field="managerSignature" canEdit={canEditManager} />
              </div>

              {/* HOD Row */}
              <div className={styles.signoffRow}>
                <span className={styles.signoffName}>Head of Department</span>
                {[
                  { field: 'hodGoalsDate', def: '2025-04-01' },
                  { field: 'hodMidDate',   def: '2025-09-30' },
                  { field: 'hodEndDate',   def: '2026-03-31' },
                ].map(({ field, def }) => (
                  <div key={field} className={styles.signoffCell}>
                    <input className={styles.signoffInput} type="date"
                      disabled={!canEditHOD}
                      value={signoffDates[field] || def}
                      onChange={(e) => setSignoffDates((p) => ({ ...p, [field]: e.target.value }))} />
                  </div>
                ))}
                <SignatureCell field="hodSignature" canEdit={canEditHOD} />
              </div>

            </div>
          </div>
        )}
      </div>

     
      {/* {!effectiveReadOnly && (
        <div className={styles.submitRow}>
          {hrFullAccess && isViewingOther ? (
            <>
            
              {!employeeSubmitted && (
                <button className={styles.submitBtn}
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  onClick={() => handleSubmit('EMPLOYEE')} disabled={submitLoading}>
                  {submitLoading ? 'Submitting...' : '👑 Submit as Employee →'}
                </button>
              )}
              {employeeSubmitted && !managerSubmitted && (
                <button className={styles.submitBtn}
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  onClick={() => handleSubmit('MANAGER')} disabled={submitLoading}>
                  {submitLoading ? 'Submitting...' : '👑 Submit as Manager →'}
                </button>
              )}
              {managerSubmitted && !hodSubmitted && (
                <button className={styles.submitBtn}
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
                  onClick={() => handleSubmit('HOD')} disabled={submitLoading}>
                  {submitLoading ? 'Submitting...' : '👑 Submit as HOD →'}
                </button>
              )}
              {hodSubmitted && (
                <span className={styles.successMsg} style={{ margin: 0 }}>
                  ✅ All stages completed
                </span>
              )}
            </>
          ) : (
            <>
              {canEditEmployee && (
                <>
                 
                  <button className={styles.submitBtn} onClick={() => handleSubmit('EMPLOYEE')} disabled={submitLoading}>
                    {submitLoading ? 'Submitting...' : 'Submit Appraisal →'}
                  </button>
                </>
              )}
              {canEditManager && (
                <button className={styles.submitBtn}
                  style={{ background: 'linear-gradient(135deg, var(--color-secondary), #5a67d8)' }}
                  onClick={() => handleSubmit('MANAGER')} disabled={submitLoading}>
                  {submitLoading ? 'Submitting...' : 'Submit Manager Review →'}
                </button>
              )}
              {canEditHOD && (
                <button className={styles.submitBtn}
                  style={{ background: 'linear-gradient(135deg, #be185d, #db2777)' }}
                  onClick={() => handleSubmit('HOD')} disabled={submitLoading}>
                  {submitLoading ? 'Submitting...' : 'Submit HOD Review →'}
                </button>
              )}
            </>
          )}
        </div>
      )} */}

      {!effectiveReadOnly && (
  <div className={styles.submitRow}>

    {hrFullAccess && isViewingOther ? (
      // HR editing someone else's form — smart stage submit
      <>
        {!employeeSubmitted && (
          <button className={styles.submitBtn}
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            onClick={() => handleSubmit('EMPLOYEE')} disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : '👑 Submit as Employee →'}
          </button>
        )}
        {employeeSubmitted && !managerSubmitted && (
          <button className={styles.submitBtn}
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            onClick={() => handleSubmit('MANAGER')} disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : '👑 Submit as Manager →'}
          </button>
        )}
        {managerSubmitted && !hodSubmitted && (
          <button className={styles.submitBtn}
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
            onClick={() => handleSubmit('HOD')} disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : '👑 Submit as HOD →'}
          </button>
        )}
        {hodSubmitted && (
          <span className={styles.successMsg} style={{ margin: 0 }}>
            ✅ All stages completed
          </span>
        )}
      </>
    ) : (
      // Normal flow — employee/manager/HOD/HR own form
      <>
        {/* Employee submit — not viewing someone else, and either not yet submitted or
            (for the latest period only) still allowed to edit and re-submit */}
        {!isViewingOther && canEditEmployee && (
          <>
            <button className={styles.saveBtn} onClick={handleSaveDraft} disabled={saving}>
              {saving ? 'Saving...' : '💾 Save Draft'}
            </button>
            <button className={styles.submitBtn} onClick={() => handleSubmit('EMPLOYEE')} disabled={submitLoading}>
              {submitLoading ? 'Submitting...' : employeeSubmitted ? 'Re-Submit Appraisal →' : 'Submit Appraisal →'}
            </button>
          </>
        )}
        {/* Manager submit — only for manager/HOD viewing someone else. Reporting manager can
            re-submit on the latest period even after managerSubmitted (see canEditManager). */}
        {isViewingOther && canEditManager && !isHR && (
          <button className={styles.submitBtn}
            style={{ background: 'linear-gradient(135deg, var(--color-secondary), #5a67d8)' }}
            onClick={() => handleSubmit('MANAGER')} disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : managerSubmitted ? 'Re-Submit Manager Review →' : 'Submit Manager Review →'}
          </button>
        )}
        {/* HOD submit — only for HOD viewing someone else */}
        {isViewingOther && canEditHOD && !isHR && (
          <button className={styles.submitBtn}
            style={{ background: 'linear-gradient(135deg, #be185d, #db2777)' }}
            onClick={() => handleSubmit('HOD')} disabled={submitLoading}>
            {submitLoading ? 'Submitting...' : 'Submit HOD Review →'}
          </button>
        )}
      </>
    )}

  </div>
)}

    </div>
  );
};

export default AppraisalForm;